import moment from 'moment';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import Form from 'react-bootstrap/Form';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { toast } from '../../toasts';
import PropTypes from 'prop-types';
import { MdInfoOutline } from 'react-icons/md';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Store } from '../../Store';
import { Button, FieldLabel, FormField, Section, Select, controlClass } from '../../components/EntityList';
import creditService from '../../services/credit.service';
import { dueDays, getError, monthOptions } from '../../utils';
import { useLabels } from '../../hooks/useLabels';

const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return { ...state, loading: false };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'UPDATE_REQUEST':
		return { ...state, loadingUpdate: true };
	case 'UPDATE_SUCCESS':
		return { ...state, loadingUpdate: false };
	case 'UPDATE_FAIL':
		return { ...state, loadingUpdate: false };
	default:
		return state;
	}
};

const CreditDateEditor = ({ onClose, onSaved, initialData: _initialData = null, cancelHandler }) => {
	const { t } = useLabels();
	const { state } = useContext(Store);
	const [{ loading, loadingUpdate, error }, dispatch] = useReducer(reducer, {
		loading: true,
		loadingUpdate: false,
		error: '',
	});

	const [date, setDate] = useState(null);
	const [periodicity, setPeriodicity] = useState('');
	const [owner, setOwner] = useState(null);
	const [firstDueExpiration, setFirstDueExpiration] = useState(null);
	const [newFirstDueExpiration, setNewFirstDueExpiration] = useState(null);
	const [numMonths, setNumMonths] = useState(0);
	const [selectedMonthOption, setSelectedMonthOption] = useState('');
	const [dueDay, setDueDay] = useState('10');
	const [selectedUpdateDateOption, setSelectedUpdateDateOption] = useState('');
	const [generatedCredits, setGeneratedCredits] = useState([]);
	const [periodicityNumMonths, setPeriodicityNumMonths] = useState(0);
	const [fieldErrors, setFieldErrors] = useState({});

	const clearFieldError = (field) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	const updateDuesOptions = () => {
		if (
			newFirstDueExpiration &&
			firstDueExpiration &&
			moment
				.utc(newFirstDueExpiration)
				.startOf('month')
				.isBefore(moment.utc(firstDueExpiration).startOf('month')) &&
			generatedCredits &&
			generatedCredits.length > 0
		) {
			return ['Mover cuotas', 'Crear cuotas faltantes'];
		} else if (
			newFirstDueExpiration &&
			firstDueExpiration &&
			moment
				.utc(newFirstDueExpiration)
				.startOf('month')
				.isAfter(moment.utc(firstDueExpiration).startOf('month')) &&
			generatedCredits &&
			generatedCredits.length > 0
		) {
			return ['Mover cuotas', 'Eliminar cuotas sobrantes'];
		} else if (generatedCredits && generatedCredits.length > 0) {
			return ['Mover cuotas'];
		} else {
			return ['Crear cuotas faltantes'];
		}
	};

	useEffect(() => {
		const fetchCreditData = async () => {
			if (_initialData?._id) {
				dispatch({ type: 'FETCH_REQUEST' });
				try {
					const result = await creditService.getDetails(_initialData._id);
					const creditData = result.data;
					setDueDay(creditData.dueDay?.toString() || '10');
					setDate(creditData.date ? moment(creditData.date).toDate() : null);
					setOwner(creditData.owner);
					setPeriodicity(creditData.periodicity || '');
					setFirstDueExpiration(creditData.firstDueExpiration ? moment(creditData.firstDueExpiration).toDate() : null);
					setSelectedMonthOption(creditData.selectedMonthOption || '');
					setNumMonths(creditData.numMonths || 0);
					setGeneratedCredits(creditData.generatedCredits || []);
					setPeriodicityNumMonths(creditData.periodicityNumMonths || 0);
					dispatch({ type: 'FETCH_SUCCESS' });
				} catch (ex) {
					console.error(ex);
					dispatch({ type: 'FETCH_FAIL', payload: getError(ex) });
					toast.error(getError(ex));
				}
			}
		};
		fetchCreditData();
	}, [_initialData]);

	const generateDate = () => {
		if (date && selectedMonthOption && dueDay) {
			const currentDate = moment.utc(date, 'YYYY-MM-DD');
			let futureDate;
			if (selectedMonthOption === 'Mes actual') {
				futureDate = currentDate.clone();
			} else if (selectedMonthOption === 'Mes siguiente') {
				futureDate = currentDate.clone().add(1, 'month');
			} else if (
				selectedMonthOption === 'En tres meses' ||
				selectedMonthOption === 'En seis meses'
			) {
				const monthsToAdd = selectedMonthOption === 'En tres meses' ? 3 : 6;
				futureDate = currentDate.clone().add(monthsToAdd, 'month');
			} else if (selectedMonthOption === 'Otro' && numMonths) {
				futureDate = currentDate.clone().add(numMonths, 'month');
			} else if (selectedMonthOption === 'Otro' && !numMonths) {
				return;
			}
			if (futureDate) {
				futureDate.date(Math.min(parseInt(dueDay), futureDate.daysInMonth()));
				setNewFirstDueExpiration(moment.utc(futureDate).format('YYYY-MM-DD'));
			}
		}
	};

	useEffect(() => {
		generateDate();
	}, [date, selectedMonthOption, dueDay, numMonths]);

	const getFieldErrors = () => {
		const errors = {};
		if (!date) errors.date = REQUIRED_FIELD_MESSAGE;
		if (!dueDay) errors.dueDay = REQUIRED_FIELD_MESSAGE;
		if (!selectedMonthOption) errors.selectedMonthOption = REQUIRED_FIELD_MESSAGE;
		if (selectedMonthOption === 'Otro' && !numMonths) errors.numMonths = REQUIRED_FIELD_MESSAGE;
		if (!selectedUpdateDateOption) errors.selectedUpdateDateOption = REQUIRED_FIELD_MESSAGE;
		return errors;
	};

	const FIELD_LABELS = {
		date: t('form.creditStartDateAccent'),
		dueDay: t('form.firstDueDay'),
		selectedMonthOption: t('form.generateFrom'),
		numMonths: t('form.numMonths'),
		selectedUpdateDateOption: t('form.existingDataAction'),
	};

	const handleSave = async (event) => {
		event.preventDefault();
		if (loading || loadingUpdate) return;

		const errors = getFieldErrors();
		const missingFields = Object.keys(errors);
		if (missingFields.length > 0) {
			setFieldErrors(errors);
			const missingLabels = missingFields.map((field) => FIELD_LABELS[field] || field);
			toast.error(`${t('msg.completeFields')}: ${missingLabels.join(', ')}`);
			return;
		}
		setFieldErrors({});

		generateDate();
		if (newFirstDueExpiration && moment.utc(newFirstDueExpiration).isBefore(moment.utc(date))) {
			toast.error(t('msg.expirationBeforeDate'));
			return;
		}

		try {
			dispatch({ type: 'UPDATE_REQUEST' });
			if (!owner) {
				toast.error(t('msg.selectAccount'));
				dispatch({ type: 'UPDATE_FAIL' });
				return;
			}
			const requestData = {
				date: moment.utc(date),
				dueDay,
				selectedMonthOption,
				firstDueExpiration: moment.utc(newFirstDueExpiration),
				updateDateOption: selectedUpdateDateOption,
				updatedDateAt: moment.utc(),
				updatedDateBy: state.userInfo._id,
				updateDateActive: true,
			};
			if (selectedMonthOption === 'Otro') {
				requestData.numMonths = numMonths;
			}
			if (periodicity === 'otra') {
				requestData.periodicityNumMonths = periodicityNumMonths;
			}
			await creditService.editDate(_initialData._id, requestData);
			dispatch({ type: 'UPDATE_SUCCESS' });
			toast.success(t('msg.creditStartDateUpdated'));
			if (onSaved) {
				onSaved();
			}
			if (onClose) {
				onClose();
			}
		} catch (err) {
			dispatch({ type: 'UPDATE_FAIL' });
			toast.error(getError(err));
		}
	};

	const dueOptions = updateDuesOptions();
	const dueOptionsKey = dueOptions.join('|');

	// The select now renders an empty placeholder option, so nothing is ever
	// implicitly selected. Pre-pick the only option when there is just one (the
	// user has nothing to choose), and drop a selection that the recomputed
	// option list no longer offers — otherwise a stale value passes validation
	// and reaches the backend, which matches it against no branch.
	useEffect(() => {
		if (dueOptions.length === 1) {
			if (selectedUpdateDateOption !== dueOptions[0]) {
				setSelectedUpdateDateOption(dueOptions[0]);
			}
		} else if (selectedUpdateDateOption && !dueOptions.includes(selectedUpdateDateOption)) {
			setSelectedUpdateDateOption('');
		}
	}, [dueOptionsKey, selectedUpdateDateOption]);

	if (loading) {
		return <div className='tw:px-2 tw:py-12 tw:text-center tw:text-[13px] tw:text-ink-3'>Cargando...</div>;
	}

	return (
		<Form id='credit-date-editor-form' onSubmit={handleSave} className='tw:space-y-4'>
			{error && (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{error}
				</div>
			)}

			<Section title={t('form.creditStartDateAccent')}>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-3'>
					<FormField id='credit-date-start' label={FIELD_LABELS.date} required>
						<DatePicker
							id='credit-date-start'
							selected={date}
							onChange={(d) => {
								setDate(d);
								clearFieldError('date');
							}}
							dateFormat='dd-MM-yyyy'
							className={`form-control ${controlClass}`}
							placeholderText='dd-mm-yyyy'
							disabled={loading || loadingUpdate}
						/>
						<FieldError message={fieldErrors.date} />
					</FormField>
					<FormField id='credit-date-due-day' label={FIELD_LABELS.dueDay} required>
						<Select
							id='credit-date-due-day'
							options={dueDays}
							value={dueDay ? dueDay.toString() : ''}
							onChange={(event) => {
								setDueDay(event.target.value);
								clearFieldError('dueDay');
							}}
							disabled={loading || loadingUpdate}
						/>
						<FieldError message={fieldErrors.dueDay} />
					</FormField>
					<FormField id='credit-date-generate-from' label={FIELD_LABELS.selectedMonthOption} required>
						<Select
							id='credit-date-generate-from'
							options={monthOptions}
							placeholder={t('form.selectMonth')}
							value={selectedMonthOption}
							onChange={(event) => {
								setSelectedMonthOption(event.target.value);
								clearFieldError('selectedMonthOption');
							}}
							disabled={loading || loadingUpdate}
						/>
						<FieldError message={fieldErrors.selectedMonthOption} />
					</FormField>
					{selectedMonthOption === 'Otro' && (
						<FormField id='credit-date-num-months' label={FIELD_LABELS.numMonths} required>
							<Form.Control
								id='credit-date-num-months'
								type='number'
								min='1'
								max='240'
								className={controlClass}
								value={numMonths}
								disabled={loading || loadingUpdate}
								onChange={(event) => {
									setNumMonths(parseInt(event.target.value, 10));
									clearFieldError('numMonths');
								}}
								onInput={(event) => {
									const inputValue = parseInt(event.target.value, 10);
									const maxValue = 240;
									if (maxValue !== null && inputValue > maxValue) {
										event.target.value = maxValue;
									}
								}}
							/>
							<FieldError message={fieldErrors.numMonths} />
						</FormField>
					)}
				</div>

				{date && newFirstDueExpiration && (
					<p className='tw:mt-3 tw:mb-0 tw:text-[12.5px] tw:text-ink-2'>
						{`${t('msg.firstExpiration')}: ${moment.utc(newFirstDueExpiration).format('DD-MM-YYYY')}`}
					</p>
				)}
			</Section>

			<Section title={t('form.existingDataAction')}>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<div>
						<FieldLabel htmlFor='credit-date-update-option' required>
							<span className='tw:inline-flex tw:items-center tw:gap-1.5'>
								{FIELD_LABELS.selectedUpdateDateOption}
								<OverlayTrigger
									trigger='click'
									placement='right'
									overlay={
										<Popover id='tooltip-previous-credit' style={{ minWidth: '50%' }}>
											<Popover.Header as='h3'>A tener en cuenta:</Popover.Header>
											<Popover.Body style={{ maxHeight: '200px', overflowY: 'auto' }}>
												<ul style={{ listStyleType: 'circle', paddingLeft: '20px' }}>
													<li>
														Si selecciona Mover Cuotas, se recorren las cuotas
														existentes y se corrigen las fechas y nombres en base a la
														fecha de primer vencimiento seleccionada.
													</li>
													<li>
														Si la fecha de primer vencimiento es previa a la anterior,
														tiene la opción de: Mover cuotas o Crear cuotas faltantes.
														<li><b>Crear cuotas faltantes:</b>
															Se mantienen las fechas de las cuotas actuales, y se
															agregan cuotas faltantes al inicio.
														</li>
													</li>
													<li>
														Si la fecha de primer vencimiento es posterior a la
														anterior, tiene la opción de: Mover cuotas o Eliminar
														cuotas sobrantes.
														<li>
															<b>Eliminar cuotas sobrantes:</b>
															Se mantienen las fechas de las cuotas
															actuales, y se eliminan las cuotas sobrantes desde el
															inicio.
															ADVERTENCIA: Si las cuotas a eliminar tienen cobros relacionados, éstos se eliminaran
														</li>
													</li>
												</ul>
											</Popover.Body>
										</Popover>
									}
								>
									<span className='tw:cursor-pointer tw:text-ink-3'>
										<MdInfoOutline />
									</span>
								</OverlayTrigger>
							</span>
						</FieldLabel>
						<Select
							id='credit-date-update-option'
							options={dueOptions}
							placeholder={t('form.select')}
							value={selectedUpdateDateOption}
							onChange={(event) => {
								setSelectedUpdateDateOption(event.target.value);
								clearFieldError('selectedUpdateDateOption');
							}}
							disabled={loading || loadingUpdate}
						/>
						<FieldError message={fieldErrors.selectedUpdateDateOption} />
					</div>
				</div>
			</Section>

			<div className='tw:flex tw:justify-end tw:gap-2'>
				<Button onClick={cancelHandler || onClose} disabled={loadingUpdate}>
					{t('action.cancel')}
				</Button>
				<Button variant='primary' type='submit' disabled={loading || loadingUpdate}>
					{loadingUpdate ? 'Guardando...' : t('action.save')}
				</Button>
			</div>
		</Form>
	);
};

CreditDateEditor.propTypes = {
	onClose: PropTypes.func,
	onSaved: PropTypes.func,
	initialData: PropTypes.object,
	cancelHandler: PropTypes.func,
};

export default CreditDateEditor;
